import { useEffect, useState } from "react";
import { Package, Clock, AlertTriangle, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatCredits } from "@/lib/credit-calculator";

interface TopupBatch {
  id: string;
  credits_remaining: number;
  credits_original: number;
  purchased_at: string;
  expires_at: string;
}

export function ActiveTopupBatches() {
  const { user } = useAuth();
  const [batches, setBatches] = useState<TopupBatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchBatches = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("credit_topup_batches")
        .select("id, credits_remaining, credits_original, purchased_at, expires_at")
        .eq("user_id", user.id)
        .gt("credits_remaining", 0)
        .gt("expires_at", new Date().toISOString())
        .order("expires_at", { ascending: true });

      if (!error && data) {
        setBatches(data);
      }
      setLoading(false);
    };

    fetchBatches();
  }, [user]);

  if (loading) {
    return (
      <Card className="glass">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (batches.length === 0) return null;

  const getDaysUntilExpiry = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const getExpiryStatus = (days: number) => {
    if (days <= 30) return "destructive" as const;
    if (days <= 90) return "secondary" as const;
    return "outline" as const;
  };

  return (
    <Card className="glass">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          Active Top-Up Batches
        </CardTitle>
        <CardDescription>
          Batches are consumed in order — earliest expiry first
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {batches.map((batch, index) => {
          const daysLeft = getDaysUntilExpiry(batch.expires_at);
          const usedPercent = ((batch.credits_original - batch.credits_remaining) / batch.credits_original) * 100;
          const isNext = index === 0;

          return (
            <div
              key={batch.id}
              className={`p-3 rounded-lg border transition-colors ${
                isNext ? "border-primary/40 bg-primary/5" : "border-border"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {isNext && (
                    <Badge variant="default" className="text-xs">
                      Using Now
                    </Badge>
                  )}
                  <span className="text-sm font-medium">
                    {formatCredits(batch.credits_remaining)} / {formatCredits(batch.credits_original)} credits
                  </span>
                </div>
                <Badge variant={getExpiryStatus(daysLeft)} className="flex items-center gap-1 text-xs">
                  {daysLeft <= 30 && <AlertTriangle className="h-3 w-3" />}
                  <Clock className="h-3 w-3" />
                  {daysLeft}d left
                </Badge>
              </div>
              <Progress value={usedPercent} className="h-1.5 mb-1.5" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Purchased {new Date(batch.purchased_at).toLocaleDateString()}</span>
                <span>Expires {new Date(batch.expires_at).toLocaleDateString()}</span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
